import type { LinhaDatasBoleto } from '../../types';
import { casaBusca, chaveUc, rotuloMes, type FiltroEmpresa } from './datasFaturas.ts';

// Regras do relatório "Datas dos boletos" (Gestão): o par de datasFaturas.ts para o boleto
// que o VigorHub emite, não a conta da Equatorial. Sem DOM: a tela e o Excel usam as mesmas
// funções, e os testes rodam sob `node --test`.

export type CampoDataBoleto =
  | 'leitura_anterior' | 'leitura_atual' | 'proxima_leitura'
  | 'emissao' | 'envio' | 'vencimento' | 'pagamento';

const DE_ONDE_LEITURA =
  'Ciclo de leitura que o boleto cobra: a data gravada na fatura (a que o boleto imprime) ou, nos meses em que ela não tem, a lida do PDF da Equatorial do mesmo mês.';

/** As 7 datas na ordem da vida do boleto: o ciclo de leitura que ele cobra, depois o boleto. */
export const CAMPOS_DATA_BOLETO: { chave: CampoDataBoleto; rotulo: string; leitura: boolean; explicacao: string }[] = [
  { chave: 'leitura_anterior', rotulo: 'Leitura anterior', leitura: true, explicacao: DE_ONDE_LEITURA },
  { chave: 'leitura_atual', rotulo: 'Leitura atual', leitura: true, explicacao: DE_ONDE_LEITURA },
  { chave: 'proxima_leitura', rotulo: 'Próxima leitura', leitura: true, explicacao: DE_ONDE_LEITURA },
  { chave: 'emissao', rotulo: 'Emissão', leitura: false, explicacao: 'Dia do registro do boleto no banco (Sicoob ou Asaas): é a data de processamento do boleto e a que sai como Emissão no PDF.' },
  { chave: 'envio', rotulo: 'Envio ao cliente', leitura: false, explicacao: 'Dia em que o sistema mandou o boleto ao cliente. Registrado a partir de MAR/2026; antes o envio era feito fora do sistema.' },
  { chave: 'vencimento', rotulo: 'Vencimento', leitura: false, explicacao: 'Vencimento do boleto da Vigor, não o da conta da Equatorial.' },
  { chave: 'pagamento', rotulo: 'Pagamento', leitura: false, explicacao: 'Dia do pagamento registrado na baixa do boleto.' },
];

/** Leituras vazias do boleto. Avulso (acordo, parcela, multa) não tem leitura: nunca falta. */
export function leiturasFaltandoBoleto(l: LinhaDatasBoleto): string[] {
  if (l.avulso) return [];
  return CAMPOS_DATA_BOLETO.filter((c) => c.leitura && !l[c.chave]).map((c) => c.rotulo);
}

/** Boleto mensal com as 3 datas de leitura. */
export function leiturasCompletasBoleto(l: LinhaDatasBoleto): boolean {
  return !l.avulso && leiturasFaltandoBoleto(l).length === 0;
}

export const SEM_STATUS = 'Sem status';

/** `status_boleto_vigor` como a tela e a planilha mostram. */
export const rotuloStatus = (l: LinhaDatasBoleto): string => (l.status || '').trim() || SEM_STATUS;

/** Pagos primeiro, depois o que ainda corre, depois o que saiu do fluxo normal. */
const ORDEM_STATUS = ['Pago', 'Quitado', 'Em aberto', 'Vencido', 'Parcelado', 'Cancelado', SEM_STATUS];

/** Os status presentes, na ordem de ORDEM_STATUS; um status novo no banco vai para o fim. */
export function statusOrdenados(porStatus: Record<string, number>): string[] {
  const pos = (s: string) => (ORDEM_STATUS.includes(s) ? ORDEM_STATUS.indexOf(s) : ORDEM_STATUS.length);
  return Object.keys(porStatus)
    .filter((s) => porStatus[s] > 0)
    .sort((a, b) => pos(a) - pos(b) || a.localeCompare(b));
}

export type FiltroTipo = 'todos' | 'mensais' | 'avulsos';

export interface FiltrosBoletos {
  busca?: string;
  empresa?: FiltroEmpresa;
  tipo?: FiltroTipo;
  /** '2026-01' (inclusive) */
  mesDe?: string;
  /** '2026-09' (inclusive) */
  mesAte?: string;
}

export function filtrarBoletos(linhas: LinhaDatasBoleto[], f: FiltrosBoletos): LinhaDatasBoleto[] {
  return linhas.filter((l) => {
    if (f.mesDe && l.mes_iso < f.mesDe) return false;
    if (f.mesAte && l.mes_iso > f.mesAte) return false;
    if (f.empresa === 'vigor' && l.empresa) return false;
    if (f.empresa === 'parceiras' && !l.empresa) return false;
    if (f.tipo === 'mensais' && l.avulso) return false;
    if (f.tipo === 'avulsos' && !l.avulso) return false;
    return f.busca ? casaBusca(l, f.busca) : true;
  });
}

export interface CoberturaMesBoleto {
  mesIso: string;
  mes: string;
  total: number;
  comEmissao: number;
  /** Boletos do mês que não são avulsos: a base da fração de leituras. */
  mensais: number;
  leiturasCompletas: number;
}

/** Boletos por mês e quantos têm emissão e as 3 leituras — base do gráfico de cobertura. */
export function coberturaBoletosPorMes(linhas: LinhaDatasBoleto[]): CoberturaMesBoleto[] {
  const mapa = new Map<string, CoberturaMesBoleto>();
  for (const l of linhas) {
    let c = mapa.get(l.mes_iso);
    if (!c) {
      c = { mesIso: l.mes_iso, mes: rotuloMes(l.mes_iso), total: 0, comEmissao: 0, mensais: 0, leiturasCompletas: 0 };
      mapa.set(l.mes_iso, c);
    }
    c.total++;
    if (l.emissao) c.comEmissao++;
    if (!l.avulso) c.mensais++;
    if (leiturasCompletasBoleto(l)) c.leiturasCompletas++;
  }
  return [...mapa.values()].sort((a, b) => a.mesIso.localeCompare(b.mesIso));
}

export interface ResumoBoletos {
  boletos: number;
  ucs: number;
  meses: number;
  comEmissao: number;
  mensais: number;
  leiturasCompletas: number;
  porStatus: Record<string, number>;
}

export function resumirBoletos(linhas: LinhaDatasBoleto[]): ResumoBoletos {
  const ucs = new Set<string>();
  const meses = new Set<string>();
  const porStatus: Record<string, number> = {};
  let comEmissao = 0;
  let mensais = 0;
  let completas = 0;
  for (const l of linhas) {
    ucs.add(chaveUc(l));
    meses.add(l.mes_iso);
    if (l.emissao) comEmissao++;
    if (!l.avulso) mensais++;
    if (leiturasCompletasBoleto(l)) completas++;
    const s = rotuloStatus(l);
    porStatus[s] = (porStatus[s] || 0) + 1;
  }
  return { boletos: linhas.length, ucs: ucs.size, meses: meses.size, comEmissao, mensais, leiturasCompletas: completas, porStatus };
}
